import { FEATURE_ENTITLEMENT_KEYS, FEATURE_ENTITLEMENT_SOURCES, parseAiProviderType } from "./ai";
import { expectArray, expectBoolean, expectOptionalString, expectRecord, expectString } from "./schema";
import { parseTenantServiceCategory, parseTenantUiLabelProfile } from "./tenant-metadata";
export const TENANT_PUBLIC_STATUSES = ["public", "private"];
export const INTEGRATION_CONFIG_STATUSES = ["configured", "notConfigured"];
export const LINE_OA_STATUSES = ["disabled", "connected", "notConfigured"];
export const MAIL_DELIVERY_DRIVERS = ["googleWorkspaceSmtpRelay", "memory", "disabled"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
function parseSafeNumber(value, fallback = 0) {
    const numeric = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
}
function parseFeatureEntitlementLimits(value, path) {
    const record = expectRecord(value, path);
    return {
        monthlyRequests: parseSafeNumber(record.monthlyRequests),
        monthlyTokens: parseSafeNumber(record.monthlyTokens),
        maxAttachmentBytes: parseSafeNumber(record.maxAttachmentBytes)
    };
}
export function parseTenantPublicStatus(value, path = "tenantPublicStatus") {
    return parseStringEnum(value, TENANT_PUBLIC_STATUSES, path);
}
export function parseIntegrationConfigStatus(value, path = "integrationConfigStatus") {
    return parseStringEnum(value, INTEGRATION_CONFIG_STATUSES, path);
}
export function parseLineOaStatus(value, path = "lineOaStatus") {
    return parseStringEnum(value, LINE_OA_STATUSES, path);
}
export function parseMailDeliveryDriver(value, path = "mailDeliveryDriver") {
    return parseStringEnum(value, MAIL_DELIVERY_DRIVERS, path);
}
export function parseAdminTenantSettings(value, path = "adminTenantSettings") {
    const record = expectRecord(value, path);
    return {
        tenantId: expectString(record.tenantId, `${path}.tenantId`),
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        brandName: expectOptionalString(record.brandName, `${path}.brandName`),
        serviceCategory: parseTenantServiceCategory(record.serviceCategory, `${path}.serviceCategory`),
        uiLabelProfile: parseTenantUiLabelProfile(record.uiLabelProfile, `${path}.uiLabelProfile`),
        timezone: expectString(record.timezone, `${path}.timezone`),
        region: expectOptionalString(record.region, `${path}.region`),
        publicStatus: parseTenantPublicStatus(record.publicStatus, `${path}.publicStatus`),
        oaMode: expectOptionalString(record.oaMode, `${path}.oaMode`),
        contactEmail: expectOptionalString(record.contactEmail, `${path}.contactEmail`),
        contactPhone: expectOptionalString(record.contactPhone, `${path}.contactPhone`),
        websiteUrl: expectOptionalString(record.websiteUrl, `${path}.websiteUrl`),
        publicDescription: expectOptionalString(record.publicDescription, `${path}.publicDescription`),
        status: expectString(record.status, `${path}.status`),
        currentPlanId: expectOptionalString(record.currentPlanId, `${path}.currentPlanId`),
        setupStatus: expectString(record.setupStatus, `${path}.setupStatus`),
        setupCompletedAt: expectOptionalString(record.setupCompletedAt, `${path}.setupCompletedAt`),
        createdAt: expectOptionalString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectOptionalString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseUpdateAdminTenantSettingsRequest(value, path = "updateAdminTenantSettingsRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "brandName")) {
        parsed.brandName = expectOptionalString(record.brandName, `${path}.brandName`);
    }
    if (hasOwn(record, "timezone")) {
        parsed.timezone = expectString(record.timezone, `${path}.timezone`);
    }
    if (hasOwn(record, "publicStatus")) {
        parsed.publicStatus = parseTenantPublicStatus(record.publicStatus, `${path}.publicStatus`);
    }
    if (hasOwn(record, "contactEmail")) {
        parsed.contactEmail = expectOptionalString(record.contactEmail, `${path}.contactEmail`);
    }
    if (hasOwn(record, "contactPhone")) {
        parsed.contactPhone = expectOptionalString(record.contactPhone, `${path}.contactPhone`);
    }
    if (hasOwn(record, "websiteUrl")) {
        parsed.websiteUrl = expectOptionalString(record.websiteUrl, `${path}.websiteUrl`);
    }
    if (hasOwn(record, "publicDescription")) {
        parsed.publicDescription = expectOptionalString(record.publicDescription, `${path}.publicDescription`);
    }
    return parsed;
}
export function parseAdminFeatureEntitlementItem(value, path = "adminFeatureEntitlementItem") {
    const record = expectRecord(value, path);
    return {
        featureKey: parseStringEnum(record.featureKey, FEATURE_ENTITLEMENT_KEYS, `${path}.featureKey`),
        enabled: expectBoolean(record.enabled, `${path}.enabled`),
        planId: expectOptionalString(record.planId, `${path}.planId`),
        source: parseStringEnum(record.source, FEATURE_ENTITLEMENT_SOURCES, `${path}.source`),
        validFrom: expectOptionalString(record.validFrom, `${path}.validFrom`),
        validTo: expectOptionalString(record.validTo, `${path}.validTo`),
        limits: record.limits === undefined || record.limits === null
            ? {
                monthlyRequests: 0,
                monthlyTokens: 0,
                maxAttachmentBytes: 0
            }
            : parseFeatureEntitlementLimits(record.limits, `${path}.limits`)
    };
}
export function parseAdminFeatureEntitlementsResponse(value, path = "adminFeatureEntitlementsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminFeatureEntitlementItem, `${path}.items`)
    };
}
export function parseAdminIntegrationSettings(value, path = "adminIntegrationSettings") {
    const record = expectRecord(value, path);
    const line = expectRecord(record.line, `${path}.line`);
    const mail = expectRecord(record.mail, `${path}.mail`);
    const storage = expectRecord(record.storage, `${path}.storage`);
    const ai = expectRecord(record.ai, `${path}.ai`);
    const billing = expectRecord(record.billing, `${path}.billing`);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        line: {
            loginStatus: parseIntegrationConfigStatus(line.loginStatus, `${path}.line.loginStatus`),
            channelIdStatus: parseIntegrationConfigStatus(line.channelIdStatus, `${path}.line.channelIdStatus`),
            liffIdSummary: expectOptionalString(line.liffIdSummary, `${path}.line.liffIdSummary`),
            miniAppBaseUrl: expectOptionalString(line.miniAppBaseUrl, `${path}.line.miniAppBaseUrl`),
            oaMode: parseLineOaStatus(line.oaMode, `${path}.line.oaMode`),
            tenantUrl: expectString(line.tenantUrl, `${path}.line.tenantUrl`),
            systemManagedMessage: expectOptionalString(line.systemManagedMessage, `${path}.line.systemManagedMessage`)
        },
        mail: {
            operationalEmailEnabled: expectBoolean(mail.operationalEmailEnabled, `${path}.mail.operationalEmailEnabled`),
            fromEmail: expectOptionalString(mail.fromEmail, `${path}.mail.fromEmail`),
            replyToEmail: expectOptionalString(mail.replyToEmail, `${path}.mail.replyToEmail`),
            deliveryDriver: parseMailDeliveryDriver(mail.deliveryDriver, `${path}.mail.deliveryDriver`),
            lastSentAt: expectOptionalString(mail.lastSentAt, `${path}.mail.lastSentAt`),
            lastFailedAt: expectOptionalString(mail.lastFailedAt, `${path}.mail.lastFailedAt`),
            lastProcessedAt: expectOptionalString(mail.lastProcessedAt, `${path}.mail.lastProcessedAt`),
            queuedCount: parseSafeNumber(mail.queuedCount),
            processingCount: parseSafeNumber(mail.processingCount),
            sentCount: parseSafeNumber(mail.sentCount),
            failedCount: parseSafeNumber(mail.failedCount),
            totalCount: parseSafeNumber(mail.totalCount),
            retryableCount: parseSafeNumber(mail.retryableCount),
            systemManagedMessage: expectOptionalString(mail.systemManagedMessage, `${path}.mail.systemManagedMessage`)
        },
        storage: {
            videoUploadEnabled: expectBoolean(storage.videoUploadEnabled, `${path}.storage.videoUploadEnabled`),
            publicEventAttachmentsEnabled: expectBoolean(storage.publicEventAttachmentsEnabled, `${path}.storage.publicEventAttachmentsEnabled`),
            bucketConfigured: expectBoolean(storage.bucketConfigured, `${path}.storage.bucketConfigured`),
            bucketDriver: parseStringEnum(storage.bucketDriver, ["memory", "gcs", "disabled"], `${path}.storage.bucketDriver`),
            maxVideoUploadSizeBytes: parseSafeNumber(storage.maxVideoUploadSizeBytes),
            maxPublicEventAttachmentBytes: parseSafeNumber(storage.maxPublicEventAttachmentBytes),
            supportedFileTypes: expectArray(storage.supportedFileTypes, (item, itemPath) => expectString(item, itemPath), `${path}.storage.supportedFileTypes`),
            systemManagedMessage: expectOptionalString(storage.systemManagedMessage, `${path}.storage.systemManagedMessage`)
        },
        ai: {
            featuresEnabled: expectBoolean(ai.featuresEnabled, `${path}.ai.featuresEnabled`),
            provider: ai.provider === "disabled"
                ? "disabled"
                : parseAiProviderType(ai.provider, `${path}.ai.provider`),
            aiAssistantEnabled: expectBoolean(ai.aiAssistantEnabled, `${path}.ai.aiAssistantEnabled`),
            aiFamilyAccountNameSuggestionEnabled: expectBoolean(ai.aiFamilyAccountNameSuggestionEnabled, `${path}.ai.aiFamilyAccountNameSuggestionEnabled`),
            systemManagedMessage: expectOptionalString(ai.systemManagedMessage, `${path}.ai.systemManagedMessage`)
        },
        billing: {
            billingReportsEnabled: expectBoolean(billing.billingReportsEnabled, `${path}.billing.billingReportsEnabled`),
            billingManagementEnabled: expectBoolean(billing.billingManagementEnabled, `${path}.billing.billingManagementEnabled`),
            locked: expectBoolean(billing.locked, `${path}.billing.locked`),
            systemManagedMessage: expectOptionalString(billing.systemManagedMessage, `${path}.billing.systemManagedMessage`)
        }
    };
}
